import { isDefined } from "@clipboard-health/util-ts";
import { FirebaseUpdateError } from "@src/appV2/Auth/errors";
import { initializeApp } from "firebase/app";
import {
  Auth,
  AuthError,
  Persistence,
  RecaptchaVerifier,
  User,
  browserLocalPersistence,
  inMemoryPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  signInWithCustomToken,
  signInWithEmailLink,
  signOut,
  updateEmail,
  updateProfile,
} from "firebase/auth";

import { PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME } from "../../../../appV2/App/useSetPreferredPersistenceForAuth/constants";
import { isTestNodeEnvironment } from "../../../../appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "../../../../appV2/lib/analytics";
import { isCapacitorPlatform } from "../../../../appV2/lib/utils/capacitor/platformUtils";

interface FirebaseResponse {
  error: string | null;
}

// We get the persistence preference from local storage.
// The item is set in local storage when the app starts, and is fetched from a feature flag
// If the value of feature flag has changed, the persistence is changed only on next start of app
function getPersistencePreference(): Persistence[] {
  const preferredPersistence = localStorage.getItem(PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME);
  if (preferredPersistence === "indexeddb") {
    return [indexedDBLocalPersistence];
  }

  return [browserLocalPersistence];
}

export class FirebaseAuthentication {
  private firebaseAuth: Auth;
  private recaptchaVerifier: RecaptchaVerifier | null;

  constructor(firebaseConfig: Record<string, string>) {
    const firebaseApp = initializeApp(firebaseConfig);
    this.recaptchaVerifier = null;
    if (!isTestNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.SET_PREFERRED_PERSISTENCE_FOR_FIREBASE_AUTH, {
        authPersistence: localStorage.getItem(PREFERRED_PERSISTENCE_LOCAL_STORAGE_KEY_NAME),
      });
    }
    this.firebaseAuth = initializeAuth(firebaseApp, {
      persistence: isTestNodeEnvironment() ? [inMemoryPersistence] : getPersistencePreference(),
    });
  }

  async initRecaptchaVerifier(
    container = "recaptcha-container",
    captchaRef: HTMLElement | null | undefined
  ) {
    if (isCapacitorPlatform()) {
      return;
    }
    if (!captchaRef) {
      return;
    }

    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }

    captchaRef.innerHTML = `<div id="${container}"></div>`;

    this.recaptchaVerifier = new RecaptchaVerifier(this.firebaseAuth, container, {
      size: "invisible",
    });

    await this.recaptchaVerifier.render();
  }

  async signInWithCustomToken(token: string) {
    return await signInWithCustomToken(this.firebaseAuth, token);
  }

  private onEmailVerified = (): FirebaseResponse => {
    localStorage.removeItem("emailToVerify");
    return { error: null };
  };

  async signInWithEmailLink(link?: string, email?: string): Promise<FirebaseResponse> {
    const emailToLogin = email || localStorage.getItem("emailToVerify");
    const url = link || window.location.href;

    if (!isDefined(emailToLogin)) {
      throw new Error("Email is not defined. Cannot sign in with email link.");
    }

    return signInWithEmailLink(this.firebaseAuth, emailToLogin, url)
      .then(this.onEmailVerified)
      .catch(this.onFirebaseError);
  }

  private onFirebaseError = (error: AuthError): FirebaseResponse => {
    switch (error.code) {
      case "auth/user-disabled":
        return {
          error:
            "Your account has been deactivated, if you believe this is in error please call or text us.",
        };

      case "auth/network-request-failed":
        return {
          error: "Network Connectivity error. Check your network connection and try again later.",
        };

      case "auth/quota-exceeded":
        return {
          error: "Verification code error. Try again after after an hour.",
        };

      case "auth/expired-action-code":
        return {
          error: "Verification code expired. Try requesting again",
        };

      case "auth/invalid-email":
        return {
          error: "Invalid email address",
        };

      case "auth/invalid-verification-code":
        return {
          error: "Invalid verification code",
        };

      case "auth/invalid-action-code":
        return {
          error:
            "Link verification failed. Please try again or contact Support if the issue persists.",
        };

      default:
        return {
          error: `Received an error - ${error.code} | ${error.message}`,
        };
    }
  };

  get currentUser() {
    return this.firebaseAuth.currentUser;
  }

  async updateCurrentUserName(name: string) {
    const user = this.firebaseAuth.currentUser;
    if (!isDefined(user)) {
      throw new Error("User is not defined. Cannot update user name.");
    }

    await updateProfile(user, { displayName: name });
  }

  async updateCurrentUserEmail(email: string): Promise<void> {
    const user = this.firebaseAuth.currentUser;
    if (!isDefined(user)) {
      throw new Error("User is not defined. Cannot update user email.");
    }

    try {
      await updateEmail(user, email);
    } catch (error: any) {
      throw new FirebaseUpdateError(error.code);
    }
  }

  onIdTokenChanged(
    callback: (user: User | null) => Promise<void>,
    errorCallback: (error: any) => void
  ) {
    return this.firebaseAuth.onIdTokenChanged(callback, errorCallback);
  }

  signOut() {
    return signOut(this.firebaseAuth);
  }
}
